import React, { useState, useEffect, Suspense } from 'react';
import {
  PlusCircleOutlined,
  MinusCircleOutlined,
  SaveOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { Row, Col, Form, InputNumber, Button, Modal, notification, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import userHaveRolePermission from 'helpers/userHaveRolePermission';
import { PAGE_URLS } from 'helpers/endpoints';
import {
  updateAllZoneDeliveryFees,
  updateAllZoneMinimumCartAmount,
} from '../../../../store/requests/zone';
import { setLoading } from 'store/requests/routing';
import { STATUS } from '../../../../helpers/constants';
import ImportAllZoneUpdate from 'components/ImportExcel/ImportAllZonesUpdate';

const AllZoneUpdate = ({ route }) => {
  const history = useHistory();
  const { isViewEnabled, isCrudEnabled } = userHaveRolePermission(route?.name);
  if (!isViewEnabled) {
    history.push(`${PAGE_URLS.AUTH}${PAGE_URLS.CONTROL}`);
  }
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const watchMinimumCartAmount = Form.useWatch('minimumCartAmount', form);
  const watchDeliveryFees = Form.useWatch('deliveryFees', form) || [];
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  useEffect(() => {
    const isDeliveryFeesEmpty =
      watchDeliveryFees.length === 0 ||
      watchDeliveryFees.every(
        (fee) =>
          (fee.minCartAmount === null ||
            fee.minCartAmount === 0 ||
            fee.minCartAmount === undefined) &&
          (fee.maxCartAmount === null || fee.maxCartAmount === undefined) &&
          (fee.deliveryFee === null || fee.deliveryFee === undefined)
      );

    const isDeliveryFeesValid =
      watchDeliveryFees.length > 0 &&
      watchDeliveryFees.every(
        (fee) =>
          fee.minCartAmount !== null &&
          fee.minCartAmount !== undefined &&
          fee.maxCartAmount !== null &&
          fee.maxCartAmount !== undefined &&
          fee.deliveryFee !== null &&
          fee.deliveryFee !== undefined
      );

    const isMinimumCartAmountValid =
      watchMinimumCartAmount !== null &&
      watchMinimumCartAmount !== undefined &&
      watchMinimumCartAmount !== '';

    if (
      (isMinimumCartAmountValid && isDeliveryFeesEmpty) ||
      isDeliveryFeesValid
    ) {
      setIsButtonDisabled(false);
    } else {
      setIsButtonDisabled(true);
    }
  }, [watchMinimumCartAmount, watchDeliveryFees]);

  const notificationSave = (type, descriptionKey) => {
    notification[type]({
      message: t('processCompleted'),
      description: t(descriptionKey),
    });
  };

  const handleAdd = (fields, form, add) => {
    if (fields.length > 0) {
      const lastField = fields[fields.length - 1];
      form
        .validateFields([
          ['deliveryFees', lastField.name, 'minCartAmount'],
          ['deliveryFees', lastField.name, 'maxCartAmount'],
          ['deliveryFees', lastField.name, 'deliveryFee'],
        ])
        .then(() => {
          const lastItem = form.getFieldValue(['deliveryFees', lastField.name]);
          add({
            minCartAmount: lastItem
              ? Number(lastItem.maxCartAmount) + 0.01
              : 0.0,
            maxCartAmount: null,
            deliveryFee: null,
          });
        })
        .catch(() => {});
    } else {
      add({
        minCartAmount: 0.0,
        maxCartAmount: null,
        deliveryFee: null,
      });
    }
  };

  const handleSave = async () => {
    setLoading(true);

    const deliveryFeesPayload = form.getFieldValue('deliveryFees') || [];
    const minimumCartAmountPayload = form.getFieldValue('minimumCartAmount');

    const validDeliveryFees = deliveryFeesPayload.filter(
      (fee) =>
        fee.minCartAmount !== null &&
        fee.minCartAmount !== undefined &&
        fee.maxCartAmount !== null &&
        fee.maxCartAmount !== undefined &&
        fee.deliveryFee !== null &&
        fee.deliveryFee !== undefined
    );

    Modal.confirm({
      title: t('takeFewMinutes'),
      content: t('allZoneUpdateWarning'),
      okText: t('yes'),
      cancelText: t('no'),
      centered: true,
      width: 600,
      onOk: async () => {
        try {
          const promises = [];
          let updatedMinimumCartAmount = false;
          let updatedDeliveryFees = false;

          if (
            minimumCartAmountPayload !== undefined &&
            minimumCartAmountPayload !== null
          ) {
            promises.push(
              updateAllZoneMinimumCartAmount(minimumCartAmountPayload).then(
                () => {
                  updatedMinimumCartAmount = true;
                }
              )
            );
          }
          if (validDeliveryFees.length > 0) {
            promises.push(
              updateAllZoneDeliveryFees({
                zoneDeliveryFees: validDeliveryFees,
              }).then(() => {
                updatedDeliveryFees = true;
              })
            );
          }

          if (promises.length > 0) {
            await Promise.all(promises);
            if (updatedMinimumCartAmount) {
              notificationSave(
                STATUS.SUCCESS,
                'minimumCartAmountSuccessfullyUpdated'
              );
            }
            if (updatedDeliveryFees) {
              notificationSave(
                STATUS.SUCCESS,
                'deliveryFeesSuccessfullyUpdated'
              );
            }
            form.resetFields();
          }
        } catch (error) {
        } finally {
          setLoading(false);
          Modal.destroyAll();
        }
      },
      onCancel: () => {
        setLoading(false);
      },
    });
  };

  const renderActionButtons = () => {
    return (
      <Tooltip title={t('import')}>
        {isCrudEnabled && (
          <Button
            type=""
            shape="round"
            icon={<UploadOutlined />}
            onClick={() => setIsImportModalOpen(true)}
            style={{ marginBottom: '15px', marginRight: '3px' }}
          />
        )}
      </Tooltip>
    );
  };

  return (
    <div style={{ position: 'relative' }}>
    <Row
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'start',
      }}
    >
      <Col>{renderActionButtons()}</Col>
    </Row>
    <Form
      name="basic"
      form={form}
      initialValues={{
        deliveryFees: [
          {
            minCartAmount: 0.0,
            maxCartAmount: null,
            deliveryFee: null,
          },
        ],
      }}
    >
      <Row>
        <Col span={14}>
          <p
            style={{
              marginBottom: '20px',
              fontWeight: 'bold',
              fontSize: '16px',
            }}
          >
            {t('flatDeliveryFee')}
          </p>
          <Form.List name="deliveryFees">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field, index) => (
                  <Row
                    key={field.key}
                    gutter={20}
                    align="end"
                    style={{ marginBottom: '16px' }}
                  >
                    <Col span={8}>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <label style={{ marginBottom: '8px' }}>
                          {t('minimumCartAmount')}
                        </label>
                        <Form.Item
                          {...field}
                          name={[field.name, 'minCartAmount']}
                          fieldKey={[field.fieldKey, 'minCartAmount']}
                          rules={[
                            {
                              required: true,
                              message: t('alert.fillField'),
                            },
                          ]}
                          style={{ margin: 0 }}
                        >
                          <InputNumber
                            min={0}
                            size="medium"
                            step={0.1}
                            maxLength={11}
                            style={{ width: '100%' }}
                            disabled={index > 0}
                            formatter={(value) =>
                              value
                                ? value.replace(/(\.\d{2})\d+/, '$1')
                                : value
                            }
                            parser={(value) => value.replace(/[^\d.]/g, '')}
                          />
                        </Form.Item>
                      </div>
                    </Col>
                    <Col span={8}>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <label style={{ marginBottom: '8px' }}>
                          {t('maximumCartAmount')}
                        </label>
                        <Form.Item
                          {...field}
                          name={[field.name, 'maxCartAmount']}
                          fieldKey={[field.fieldKey, 'maxCartAmount']}
                          rules={[
                            {
                              required: true,
                              message: t('alert.fillField'),
                            },
                          ]}
                          style={{ margin: 0 }}
                        >
                          <InputNumber
                            min={0}
                            size="medium"
                            step={0.1}
                            maxLength={11}
                            style={{ width: '100%' }}
                            formatter={(value) =>
                              value
                                ? value.replace(/(\.\d{2})\d+/, '$1')
                                : value
                            }
                            parser={(value) => value.replace(/[^\d.]/g, '')}
                          />
                        </Form.Item>
                      </div>
                    </Col>
                    <Col span={8}>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <label style={{ marginBottom: '8px' }}>
                          {t('deliveryFee')}
                        </label>
                        <Form.Item
                          {...field}
                          name={[field.name, 'deliveryFee']}
                          fieldKey={[field.fieldKey, 'deliveryFee']}
                          rules={[
                            {
                              required: true,
                              message: t('alert.fillField'),
                            },
                          ]}
                          style={{ margin: 0 }}
                        >
                          <InputNumber
                            min={0}
                            size="medium"
                            step={0.1}
                            maxLength={11}
                            style={{ width: '100%' }}
                            formatter={(value) =>
                              value
                                ? value.replace(/(\.\d{2})\d+/, '$1')
                                : value
                            }
                            parser={(value) => value.replace(/[^\d.]/g, '')}
                          />
                        </Form.Item>
                      </div>
                    </Col>
                  </Row>
                ))}
                <Row>
                  <Col span={24}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        marginTop: '10px',
                        marginBottom: '10px',
                        gap: '10px',
                        alignItems: 'center',
                      }}
                    >
                      {fields.length > 1 && (
                        <MinusCircleOutlined
                          style={{ fontSize: '24px', cursor: 'pointer' }}
                          onClick={() => {
                            if (fields.length > 1) {
                              remove(fields.length - 1);
                            }
                          }}
                        />
                      )}
                      <PlusCircleOutlined
                        style={{ fontSize: '24px', cursor: 'pointer' }}
                        onClick={() => handleAdd(fields, form, add)}
                      />
                    </div>
                  </Col>
                </Row>
              </>
            )}
          </Form.List>
          <p
            style={{
              marginBottom: '20px',
              fontWeight: 'bold',
              fontSize: '16px',
            }}
          >
            {t('minimumCartAmount')}
          </p>
          <Col span={8}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <label style={{ marginBottom: '8px' }}>
                {t('minimumCartAmount')}
              </label>
              <Form.Item
                name="minimumCartAmount"
                style={{ marginRight: '8px' }}
              >
                <InputNumber
                  min={0}
                  step={0.1}
                  maxLength={11}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </div>
          </Col>
          {isCrudEnabled && (
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                icon={<SaveOutlined />}
                type="primary"
                onClick={handleSave}
                disabled={isButtonDisabled}
              >
                {t('save')}
              </Button>
            </div>
          )}
        </Col>
      </Row>
    </Form>
    <Suspense fallback={<></>}>
    {isImportModalOpen && (
      <ImportAllZoneUpdate
        onCloseHandler={() => setIsImportModalOpen(false)}
        onSuccessCallback={() => {
          notificationSave(STATUS.SUCCESS, 'fileUploadSuccess');
          form.resetFields();
        }}
      />
    )}
  </Suspense>
</div>
  );
};

export default AllZoneUpdate;
